/**
 * RecordingService — Manages MediaRecorder lifecycle for audio capture.
 *
 * Provides start/stop recording, produces audio blobs, and tracks duration.
 * Works in all modern browsers via the MediaRecorder API.
 */

export interface RecordingResult {
  blob: Blob;
  mimeType: string;
  durationSeconds: number;
}

export class RecordingService {
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private stream: MediaStream | null = null;
  private startTime: number = 0;
  private durationTimer: ReturnType<typeof setInterval> | null = null;
  private onDurationUpdate: ((seconds: number) => void) | null = null;

  /** Check if MediaRecorder API is available */
  static isSupported(): boolean {
    return typeof MediaRecorder !== "undefined" && !!navigator.mediaDevices;
  }

  /** Start recording audio from the microphone */
  async startRecording(
    onDurationUpdate?: (seconds: number) => void
  ): Promise<void> {
    if (this.mediaRecorder?.state === "recording") {
      throw new Error("Already recording");
    }

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        sampleRate: { ideal: 48000 },
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    this.audioChunks = [];
    this.onDurationUpdate = onDurationUpdate ?? null;

    // Pick MIME type: prefer webm/opus, fall back to whatever is available
    const mimeType = this.selectMimeType();

    this.mediaRecorder = new MediaRecorder(this.stream, {
      mimeType,
      audioBitsPerSecond: 128000,
    });

    this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
      if (event.data.size > 0) {
        this.audioChunks.push(event.data);
      }
    };

    this.startTime = Date.now();
    this.mediaRecorder.start(1000); // 1-second chunks

    // Duration counter
    this.durationTimer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - this.startTime) / 1000);
      this.onDurationUpdate?.(elapsed);
    }, 1000);
  }

  /** Stop recording and return the audio blob */
  stopRecording(): Promise<RecordingResult> {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder || this.mediaRecorder.state !== "recording") {
        reject(new Error("Not recording"));
        return;
      }

      const mimeType = this.mediaRecorder.mimeType;

      this.mediaRecorder.onstop = () => {
        const durationSeconds = Math.floor(
          (Date.now() - this.startTime) / 1000
        );
        const blob = new Blob(this.audioChunks, { type: mimeType });

        this.cleanup();

        resolve({ blob, mimeType, durationSeconds });
      };

      this.mediaRecorder.onerror = () => {
        this.cleanup();
        reject(new Error("Recording failed"));
      };

      this.mediaRecorder.stop();
    });
  }

  /** Check if currently recording */
  isRecording(): boolean {
    return this.mediaRecorder?.state === "recording";
  }

  /** Abort recording without producing output */
  abort(): void {
    if (this.mediaRecorder?.state === "recording") {
      this.mediaRecorder.stop();
    }
    this.cleanup();
  }

  private cleanup(): void {
    if (this.durationTimer) {
      clearInterval(this.durationTimer);
      this.durationTimer = null;
    }

    // Stop all media tracks
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }

    this.mediaRecorder = null;
    this.audioChunks = [];
    this.onDurationUpdate = null;
  }

  private selectMimeType(): string {
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4",
      "audio/ogg;codecs=opus",
    ];

    for (const candidate of candidates) {
      if (MediaRecorder.isTypeSupported(candidate)) {
        return candidate;
      }
    }

    // Fallback: let browser decide
    return "";
  }
}
